/**
 * Agent-wallet ("1-click trading") logic with no React in it (#688). Research + sources:
 * docs/research/AGENT_WALLET.md.
 *
 *   enable:  fresh secp256k1 key in the browser → the user's wallet signs ONE user-signed approveAgent
 *            {agentAddress, agentName "hip4 valid_until <ms>", nonce} on its CURRENT chain → POST /exchange →
 *            verify with /info {type:"extraAgents", user} that the agent is registered.
 *   use:     orders / cancels are L1 actions signed locally by the agent key (hl/exchange.ts signL1WithAgent).
 *   revoke:  approving another agent with the same name replaces it (Hyperliquid deregisters the old one); the
 *            valid_until suffix makes Hyperliquid expire it by itself.
 */
import type { Address, Hex } from 'viem';
import { generatePrivateKey, privateKeyToAddress } from 'viem/accounts';
import type { EnvConfig } from '../config/env';
import { postExchange, signApproveAgentOnCurrentChain, type Eip1193Provider } from '../hl/exchange';
import { gatedInfoFetch } from '../hl/healthMonitor';
import type { AgentSession } from './storage';
import { dateTimeFormat } from '../i18n/format';
import { t } from '../i18n/i18n';

/** Base agent name. Named agents: 3 per account (+1 unnamed), so we take ONE named slot and replace it on re-enable. */
export const AGENT_NAME_BASE = 'hip4';
/** Default session length. Hyperliquid caps valid_until at 180 days. */
export const DEFAULT_AGENT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const MAX_AGENT_TTL_MS = 180 * 24 * 60 * 60 * 1000;

/** "hip4 valid_until 1761200000000": Hyperliquid's expiry convention (exchange-endpoint docs, "Approve an API wallet"). */
export function agentNameWithExpiry(base: string, validUntil: number): string {
  if (base.length < 1 || base.length > 16) throw new Error('agent name must be 1-16 characters');
  if (!Number.isSafeInteger(validUntil) || validUntil <= 0) throw new Error(`invalid validUntil ${validUntil}`);
  return `${base} valid_until ${validUntil}`;
}

export interface ExtraAgent {
  name: string;
  address: string;
  validUntil?: number;
}

/** POST /info {type:"extraAgents", user}: the user's approved agents [{name, address, validUntil}]. */
export async function fetchExtraAgents(
  env: Pick<EnvConfig, 'infoUrl'>,
  user: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ExtraAgent[]> {
  const res = await gatedInfoFetch(env.infoUrl, fetchImpl)(env.infoUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type: 'extraAgents', user: user.toLowerCase() }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status} from /info extraAgents`);
  const body: unknown = await res.json();
  if (!Array.isArray(body)) throw new Error('unexpected extraAgents response');
  return body.filter(
    (a): a is ExtraAgent => !!a && typeof a === 'object' && typeof (a as { address?: unknown }).address === 'string',
  );
}

export type AgentStatusCheck = 'approved' | 'missing' | 'unknown';

/**
 * Is `agentAddress` still an approved, unexpired agent of `user`? 'unknown' on network errors (keep the session;
 * /exchange will tell us if it is gone).
 */
export async function checkAgent(
  env: Pick<EnvConfig, 'infoUrl'>,
  user: string,
  agentAddress: string,
  opts: { fetchImpl?: typeof fetch; now?: number } = {},
): Promise<AgentStatusCheck> {
  let list: ExtraAgent[];
  try {
    list = await fetchExtraAgents(env, user, opts.fetchImpl);
  } catch {
    return 'unknown';
  }
  const a = list.find((x) => x.address.toLowerCase() === agentAddress.toLowerCase());
  if (!a) return 'missing';
  if (typeof a.validUntil === 'number' && a.validUntil <= (opts.now ?? Date.now())) return 'missing';
  return 'approved';
}

/**
 * /exchange said the signer is not a (known) agent of any user, i.e. the agent was replaced, expired or pruned.
 * Hyperliquid's text: "User or API Wallet 0x… does not exist." Only meaningful for agent-signed requests.
 */
export function isAgentMissingError(message: string): boolean {
  return /(user or api wallet|api wallet|agent).{0,60}(does not exist|not found|not approved|expired)/i.test(message);
}

/** approveAgent was accepted but /info never listed the agent. */
export class AgentNotRegisteredError extends Error {
  constructor() {
    super(t('agent.not_listed'));
    this.name = 'AgentNotRegisteredError';
  }
}

/** /exchange refused approveAgent (status "err" or HTTP error). */
export class AgentApproveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AgentApproveError';
  }
}

export interface EnableDeps {
  provider: Eip1193Provider;
  account: Hex;
  env: EnvConfig;
  now?: () => number;
  ttlMs?: number;
  fetchImpl?: typeof fetch;
  /** Test hook: key generator (default viem generatePrivateKey = crypto.getRandomValues). */
  generateKey?: () => Hex;
  /** extraAgents verification attempts + delay (registration is synchronous on HL; a short retry covers lag). */
  verify?: { tries: number; delayMs: number };
}

/**
 * Generate an agent key, have the wallet sign approveAgent on its current chain, submit it and verify the agent is
 * listed. Throws the wallet error unchanged (user rejection etc.), AgentApproveError for an /exchange refusal and
 * AgentNotRegisteredError when /info does not list it. Returns the session to store.
 */
export async function enableAgent(d: EnableDeps): Promise<AgentSession> {
  const now = d.now ?? Date.now;
  const ttl = Math.min(d.ttlMs ?? DEFAULT_AGENT_TTL_MS, MAX_AGENT_TTL_MS);
  const agentKey = (d.generateKey ?? generatePrivateKey)();
  const agentAddress: Address = privateKeyToAddress(agentKey);
  const nonce = now();
  const validUntil = nonce + ttl;
  const agentName = agentNameWithExpiry(AGENT_NAME_BASE, validUntil);

  const { payload } = await signApproveAgentOnCurrentChain(d.provider, d.account, agentAddress, agentName, nonce, d.env);

  let resp;
  try {
    resp = await postExchange(d.env, payload, d.fetchImpl);
  } catch (e) {
    throw new AgentApproveError(e instanceof Error ? e.message : String(e));
  }
  if (resp.status !== 'ok') {
    throw new AgentApproveError(typeof resp.response === 'string' ? resp.response : JSON.stringify(resp.response));
  }

  const v = d.verify ?? { tries: 3, delayMs: 700 };
  let state: AgentStatusCheck = 'unknown';
  for (let i = 0; i < v.tries; i++) {
    if (i > 0) await new Promise((r) => setTimeout(r, v.delayMs));
    state = await checkAgent(d.env, d.account, agentAddress, { fetchImpl: d.fetchImpl, now: now() });
    if (state === 'approved') break;
  }
  if (state !== 'approved') throw new AgentNotRegisteredError();

  return {
    v: 1,
    env: d.env.name,
    user: d.account.toLowerCase(),
    agentAddress,
    agentKey,
    agentName,
    validUntil,
    createdAt: nonce,
  };
}

/** "Oct 24, 14:05" style local expiry for the indicator. */
export function formatExpiry(ms: number): string {
  return dateTimeFormat({ month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(ms));
}
